// ------------- Component for the macro panel of the application -------------------
// ----------------------------------------------------------------------------------

// import necessary libraries and hooks
import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useMacroData } from '../hooks/useMacroData';
import { useMacroHistory } from '../hooks/useMacroHistory';
import './MetricsPanel.css';

// Component to display economic indicators and their historical trends
const MetricsPanel = ({ stockData, timeframe = 'MAX' }) => {
  const { data: macroData, loading, error, refetch } = useMacroData(timeframe);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const { historyData, loading: historyLoading } = useMacroHistory(selectedIndicator, timeframe);

  // helper functions to format values, names, and descriptions for display
  const formatValue = (name, value) => {
    if (['FED_FUNDS', '10Y_TREASURY', '2Y_TREASURY', 'YIELD_CURVE', 'UNEMPLOYMENT'].includes(name)) {
      return `${value.toFixed(3)}%`;
    }
    return value.toFixed(3);
  };

  // format the names from the dataset to be more user-friendly
  const formatName = (name) => {
    const names = {
      'FED_FUNDS': 'Fed Funds Rate',
      '10Y_TREASURY': '10Y Treasury Yield',
      '2Y_TREASURY': '2Y Treasury Yield',
      'YIELD_CURVE': 'Yield Curve',
      'CPI': 'CPI',
      'CORE_CPI': 'Core CPI',
      'UNEMPLOYMENT': 'Unemployment Rate',
      'VIX': 'Volatility (VIX)',
      'CPI_Inflation': 'CPI Inflation',
      'Core_Inflation': 'Core Inflation',
      'Jobless_Trend': 'Jobless Claims Trend',
      'Fed_Policy_Stance': 'Fed Policy Stance',
      'Yield_10Y_Chg': '10Y Yield Change',
      'Yield_Curve_Slope': 'Yield Curve Slope',
      'VIX_Momentum': 'VIX Momentum',
      'VIX_Regime': 'VIX Regime',
      'Tech_Sector_Return': 'Tech Sector Return',
      'Tech_Sector_Momentum': 'Tech Momentum',
    };
    return names[name] || name;
  };

  // format the descriptions from the dataset to be more user-friendly
  const formatDescription = (name) => {
    const descriptions = {
      'CPI_Inflation':      '6-month % change in CPI',
      'Core_Inflation':     '6-month % change in Core CPI',
      'Jobless_Trend':      '6-month % change in jobless claims',
      'Fed_Policy_Stance':  'Deviation from 1Y rolling mean rate',
      'Yield_10Y_Chg':      'Day-over-day change in 10Y yield',
      'Yield_Curve_Slope':  '10Y minus 2Y Treasury spread',
      'VIX_Momentum':       'Day-over-day change in VIX',
      'VIX_Regime':         'VIX level regime (Low/Med/High)',
      'Tech_Sector_Return': 'Daily log return of tech sector',
      'Tech_Sector_Momentum': '5-day rolling avg of tech log returns',
      'FED_FUNDS':          'Current federal funds rate',
      '10Y_TREASURY':       '10-year Treasury yield',
      '2Y_TREASURY':        '2-year Treasury yield',
      'YIELD_CURVE':        '10Y minus 2Y spread',
      'CPI':                'Consumer Price Index level',
      'CORE_CPI':           'Core CPI (ex food & energy)',
      'UNEMPLOYMENT':       'Unemployment rate',
      'VIX':                'CBOE Volatility Index level',
    };
    return descriptions[name] || null;
  };

  // render loading state
  if (loading) return (
    <div className="metrics-panel">
      <h3>Economic Indicators</h3>
      <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>Loading indicators...</div>
    </div>
  );

  // render the error rate
  if (error) return (
    <div className="metrics-panel">
      <h3>Economic Indicators</h3>
      <div style={{ textAlign: 'center', padding: '20px', color: '#EF4444' }}>
        <p>Error loading indicators</p>
        <button onClick={refetch} style={{ marginTop: '10px', padding: '8px 16px', background: '#06B6D4', color: '#0B1220', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retry</button>
      </div>
    </div>
  );

  // render empty state if no indicators are available
  if (!macroData?.indicators || Object.keys(macroData.indicators).length === 0) return (
    <div className="metrics-panel">
      <h3>Economic Indicators</h3>
      <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>No indicators available</div>
    </div>
  );

  return (
    <div className="metrics-panel">
      <div className="metrics-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3>Economic Indicators</h3> {/* Display the chart title and source information in the header of the chart */}
          <span className="metrics-source">Source: FRED</span>
        </div>
      </div>

      <div className="metrics-grid">
        {/* loop through the indicators and display each one with its value, change, and an inline chart if selected */}
        {Object.entries(macroData.indicators).map(([key, indicator]) => ( 

          // react fragment is used to group the indicator and its inline chart together without adding extra nodes
          <React.Fragment key={key}> 
            <div
              className={`metric-item ${selectedIndicator === key ? 'selected' : ''}`}
              onClick={() => setSelectedIndicator(selectedIndicator === key ? null : key)}
              title="Click to view trend"
            >
              {/* render the title and description of the indicator in a column layout with a small gap between them */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span className="metric-label">{formatName(key)}</span>
                {formatDescription(key) && ( // only show the description if it exists for the indicator
                  <span className="metric-description">{formatDescription(key)}</span>
                )}
              </div>

              {/* render the value, change, and expand/collapse icon of the indicator in a row layout with a small gap between them */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="metric-value">{formatValue(key, indicator.value)}</span>
                <span className={`metric-change ${indicator.change >= 0 ? 'positive' : 'negative'}`}>
                  {indicator.change >= 0 ? '▲' : '▼'} {Math.abs(indicator.changePercent).toFixed(2)}%
                </span>
                <span className="metric-expand-icon">{selectedIndicator === key ? '▾' : '▸'}</span>
              </div>
            </div>

            {/* Inline chart renders directly below the clicked indicator */}
            {selectedIndicator === key && (
              <div className="metric-chart-container">
                <div className="metric-chart-header">
                  <span>{formatName(key)} — Historical Trend</span>
                  <button className="metric-chart-close" onClick={() => setSelectedIndicator(null)}>✕</button>
                </div>

                {historyLoading ? ( // if the historical data is still loading, show a loading message
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '12px' }}>
                    Loading chart...
                  </div>
                ) : historyData.length === 0 ? ( // if there is no historical data, show a message indicating that
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '12px' }}>
                    No historical data available
                  </div>
                ) : ( // if there is historical data, render the line chart with the data and appropriate formatting
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={historyData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F2933" />

                      <XAxis
                        dataKey="date"
                        stroke="#9CA3AF"
                        style={{ fontSize: '10px' }}
                        tickFormatter={d => d?.substring(0, 7)}
                        interval="preserveStartEnd"
                      />

                      <YAxis
                        stroke="#9CA3AF"
                        style={{ fontSize: '10px' }}
                        domain={['auto', 'auto']}
                        width={40}
                      />

                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2933', border: '1px solid #374151', borderRadius: '4px', color: '#E5E7EB', fontSize: '11px' }}
                        formatter={(val) => [formatValue(key, val), formatName(key)]}
                      />
                      
                      <Line type="monotone" dataKey="value" stroke="#06B6D4" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default MetricsPanel;